import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ReportConfigurationPanel = ({ onGenerateReport, isGenerating }) => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedScope, setSelectedScope] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedCadence, setSelectedCadence] = useState('');
  const [includeAnnex, setIncludeAnnex] = useState(false);
  const [includeRemediation, setIncludeRemediation] = useState(true);
  const [includeHistorical, setIncludeHistorical] = useState(false);

  const reportingPeriods = [
    { value: '2024-q4', label: 'Q4 2024 (Oct - Dec 2024)' },
    { value: '2024-q3', label: 'Q3 2024 (Jul - Sep 2024)' },
    { value: '2024-h2', label: 'H2 2024 (Jul - Dec 2024)' },
    { value: '2024-annual', label: 'Annual 2024 (Jan - Dec 2024)' },
    { value: '2023-annual', label: 'Annual 2023 (Jan - Dec 2023)' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const organizationalScopes = [
    { value: 'global', label: 'Global Organization (All Entities)' },
    { value: 'eu-entities', label: 'EU Entities Only' },
    { value: 'germany', label: 'Germany Operations' },
    { value: 'france', label: 'France Operations' },
    { value: 'netherlands', label: 'Netherlands Operations' },
    { value: 'spain', label: 'Spain Operations' },
    { value: 'custom-entities', label: 'Custom Entity Selection' }
  ];

  const complianceTemplates = [
    { value: 'eu-directive-standard', label: 'EU Pay Transparency Directive (Standard)' },
    { value: 'germany-entgelttransparenz', label: 'Germany - Entgelttransparenzgesetz' },
    { value: 'france-index-egalite', label: 'France - Index Égalité Professionnelle' },
    { value: 'uk-gender-pay-gap', label: 'UK - Gender Pay Gap Reporting' },
    { value: 'netherlands-wgb', label: 'Netherlands - Wet Gelijke Behandeling' },
    { value: 'spain-igualdad', label: 'Spain - Ley de Igualdad' },
    { value: 'custom-template', label: 'Custom Compliance Template' }
  ];

  const reportingCadences = [
    { value: 'quarterly', label: 'Quarterly Reporting (250+ employees)' },
    { value: 'semi-annual', label: 'Semi-Annual Reporting (100-249 employees)' },
    { value: 'annual', label: 'Annual Reporting (50-99 employees)' },
    { value: 'biennial', label: 'Biennial Reporting (<50 employees)' },
    { value: 'custom', label: 'Custom Reporting Schedule' }
  ];

  const handleGenerateReport = () => {
    const reportConfig = {
      period: selectedPeriod,
      scope: selectedScope,
      template: selectedTemplate,
      cadence: selectedCadence,
      options: {
        includeAnnex,
        includeRemediation,
        includeHistorical
      },
      timestamp: new Date()?.toISOString()
    };
    onGenerateReport(reportConfig);
  };

  const isConfigurationComplete = selectedPeriod && selectedScope && selectedTemplate && selectedCadence;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t('complianceReportsPage.config2.sectionTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('complianceReportsPage.config2.sectionSubtitle')}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Settings" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t('complianceReportsPage.config2.euDirectiveReady')}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <Select
            label={t('complianceReportsPage.config.reportingPeriod')}
            description={t('complianceReportsPage.config.reportingPeriodHint')}
            options={reportingPeriods}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            placeholder={t('complianceReportsPage.config.choosePeriod')}
            required
          />

          <Select
            label={t('complianceReportsPage.config.orgScope')}
            description={t('complianceReportsPage.config.orgScopeHint')}
            options={organizationalScopes}
            value={selectedScope}
            onChange={setSelectedScope}
            placeholder={t('complianceReportsPage.config.chooseScope')}
            required
          />

          <Select
            label={t('complianceReportsPage.config.template')}
            description={t('complianceReportsPage.config.templateHint')}
            options={complianceTemplates}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
            placeholder={t('complianceReportsPage.config.chooseTemplate')}
            searchable
            required
          />
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <Select
            label={t('complianceReportsPage.config.cadence')}
            description={t('complianceReportsPage.config.cadenceHint')}
            options={reportingCadences}
            value={selectedCadence}
            onChange={setSelectedCadence}
            placeholder={t('complianceReportsPage.config.chooseCadence')}
            required
          />

          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{t('complianceReportsPage.config2.reportOptions')}</label>
            <div className="space-y-2 p-4 bg-muted/50 rounded-md">
              <Checkbox
                label={t('complianceReportsPage.config.includeAnnex')}
                description={t('complianceReportsPage.config.includeAnnexHint')}
                checked={includeAnnex}
                onChange={(e) => setIncludeAnnex(e?.target?.checked)}
              />
              <Checkbox
                label={t('complianceReportsPage.config.includeRemediation')}
                description={t('complianceReportsPage.config.includeRemediationHint')}
                checked={includeRemediation}
                onChange={(e) => setIncludeRemediation(e?.target?.checked)}
              />
              <Checkbox
                label={t('complianceReportsPage.config.includeHistory')}
                description={t('complianceReportsPage.config.includeHistoryHint')}
                checked={includeHistorical}
                onChange={(e) => setIncludeHistorical(e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Configuration Summary */}
      {isConfigurationComplete && (
        <div className="mt-6 p-4 bg-success/10 border border-success/20 rounded-md">
          <div className="flex items-start space-x-3">
            <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
            <div>
              <h4 className="font-medium text-success">{t('complianceReportsPage.config2.configComplete')}</h4>
              <p className="text-sm text-success/80 mt-1">
                Ready to generate {complianceTemplates?.find(t => t?.value === selectedTemplate)?.label} report for {reportingPeriods?.find(p => p?.value === selectedPeriod)?.label}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Generate Button */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Shield" size={16} />
          <span>GDPR Compliant • Audit Trail Enabled</span>
        </div>
        <Button
          variant="default"
          iconName="FileText"
          iconPosition="left"
          loading={isGenerating}
          disabled={!isConfigurationComplete || isGenerating}
          onClick={handleGenerateReport}
        >
          {isGenerating ? 'Generating Report...' : 'Generate Compliance Report'}
        </Button>
      </div>
    </div>
  );
};

export default ReportConfigurationPanel;